/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable no-unused-expressions*/

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Formik } from "formik";
import moment from 'moment';

import { GlobalAuthContext } from "store/context";
import { LoaderContext } from '@fitinsur/contexts';
import { useTextMessage } from '@fitinsur/hooks';
import { LineLabel } from './styles';

import { useGetAdminEconomicIndexersList } from "@fitinsur/services/AdminService";

import { useGetTextMessageByDomainType } from "../../../../services/TextMessageService";

import Colors from "constants/Colors";

import {
  Button,
  ButtonWithIcon,
  Text,
  ResponsiveTable,
  ListHeader
} from '@fitinsur/components';

import * as S from "./styles";

export function EconomicIndexersList({ permissions }) {
  const { getDescription } = useTextMessage();
  const { updateIsLoadingStatus } = useContext(LoaderContext);
  const authContext = useContext(GlobalAuthContext);

  const history = useHistory();
  const formik = useRef();

  const columns = [
    {
      title: getDescription("DOMAINS", 1141),
      key: "rateName",
      sortKey: 'rateName',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) =>
        <LineLabel>
          {listItem.rateName}
        </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 1143),
      key: "rateStartDate",
      sortKey: 'rateStartDate',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {moment(listItem.rateStartDate).format('MM/YYYY')}
      </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 1142),
      key: "rateValue",
      sortKey: 'rateValue',
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {listItem.rateValue}
      </LineLabel>
    },
    {
      title: getDescription("DOMAINS", 1168),
      key: "createDate",
      sortKey: 'createDate',
      notLastColumnAlign: true,
      minWidth: 14,
      maxWidth: 14,
      component: (listItem) => <LineLabel>
        {moment(listItem.createDate).format('DD/MM/YYYY')}
      </LineLabel>
    }
  ];

  const initialFilterData = [
    { label: getDescription("DOMAINS", 1141, { textFormat: 'uppercase' }) || "ÍNDICE", orderKey: 'rateName', key: 'rateName', type: 'list' },
    { label: getDescription("DOMAINS", 1143, { textFormat: 'uppercase' }) || "MÊS DE REFERÊNCIA", orderKey: 'rateStartDate', key: 'rateStartDate', type: 'date' },
    { label: getDescription("DOMAINS", 1142, { textFormat: 'uppercase' }) || "TAXA DE CORREÇÃO", orderKey: 'rateValue', key: 'rateValue', type: 'text' },
    { label: getDescription("DOMAINS", 1168, { textFormat: 'uppercase' }) || "DATA DE UPLOAD", orderKey: 'createDate', key: 'createDate', type: 'date' }
  ];

  const storedFilterOption = localStorage.getItem('econocimic-indexers-filter');
  const storedStringFilterOption = localStorage.getItem('str-economic-indexers-filter');
  let quotesUrl = '/management/58/economic-indexers' + (storedStringFilterOption ? '?' + storedStringFilterOption : '');

  const [filterData, setFilterData] = useState(storedFilterOption ? JSON.parse(storedFilterOption) : initialFilterData);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listData, setListData] = useState([]);
  const [defaultIndexersList, setDefaultIndexersList] = useState([]);
  const [paginationControls, setPaginationControls] = useState({
    "currentPage": 1,
    "numberMaxRecords": null,
    "total": null,
    "totalPages": null,
    "numberRecords": null
  });
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const initialValues = {
    filter: {
      rateName: "",
      rateStartDate: "",
      rateValue: "",
      createDate: "",
    },
  };

  const {
    loading: useGetAdminEconomicIndexersListLoading,
    request: useGetAdminEconomicIndexersListRequest,
  } = useGetAdminEconomicIndexersList();

  const {
    request: useGetTextMessageByDomainTypeRequest,
    loading: useGetTextMessageByDomainTypeLoading,
  } = useGetTextMessageByDomainType(62); // pending ENUM to remove magical numbers

  async function getEconomicIndexersListData(resetFilter) {
    const refFilterData = resetFilter ? initialFilterData : filterData;
    useGetAdminEconomicIndexersListRequest(null, {
      page: paginationControls.currentPage,
      sortBy,
      ...(refFilterData
        .filter(v => v.value)
        .reduce((a, b) => {
          a[b.key] = b.value;
          return a
        }, {}))
    }).then((resp) => {
      const { data: { value: { economicIndexers, pagination } } } = resp;

      if (paginationControls.currentPage === 1) {
        setListData(economicIndexers);
      } else {
        setListData([...listData, ...economicIndexers]);
        setCurrentPage(paginationControls.currentPage);
      }

      setTotalPages(pagination?.totalPages);
    });
  }

  function handleSortList(key, direction) {
    if (!key || key === 'action' || key === 'status') return;
    direction = direction === 1 ? 'DESC' : 'ASC';
    let sort = { key, direction };
    setSortBy(sort);
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);
    getEconomicIndexersListData();
  }

  function handleFilter(resetFilter) {
    quotesUrl = '/management/58/economic-indexers' + (localStorage.getItem('economic-indexers-filter') ? '?' + localStorage.getItem('str-economic-indexers-filter') : '')
    let e = paginationControls;
    e.currentPage = 1;
    setPaginationControls(e);

    if (resetFilter) {
      getEconomicIndexersListData(resetFilter);
      localStorage.removeItem('economic-indexers-filter')
      localStorage.removeItem('str-economic-indexers-filter')
      history.push(`/management/58/economic-indexers`)
    } else {
      getEconomicIndexersListData();
      history.push(`${quotesUrl}`)
    }
  };

  function setNextPage(paginationControls) {
    let e = paginationControls;
    e.currentPage++;
    setPaginationControls(e);
    getEconomicIndexersListData();
  }

  useEffect(() => {
     getEconomicIndexersListData();
     useGetTextMessageByDomainTypeRequest({}, { isoCode: "pt-BR", modelName: "DOMAINS"}).then(response => {
      if (response.status === 200) {
        setDefaultIndexersList(response.data.value.map(indexer => { 
          return {
            label: indexer.translatedDescription.toUpperCase(), value: indexer.translatedDescription.toUpperCase()
          }
        }));
      }
    });
  }, []);


  useEffect(() => {
    updateIsLoadingStatus(
      useGetAdminEconomicIndexersListLoading ||
      useGetTextMessageByDomainTypeLoading
    );
  }, [
    useGetAdminEconomicIndexersListLoading, useGetTextMessageByDomainTypeLoading
  ]);

  return (
    <>
      <ButtonWithIcon
        onClick={() => history.push('/management/58/economic-indexers/create')}
        icon={{ name: "plus-gray", width: '12px', height: '12px' }}
        title={getDescription("DOMAINS", 1167, { textFormat: 'uppercase' })}
      />

      <S.Content>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => (
            <Form>
              <ListHeader
                filterData={filterData}
                setFilterData={setFilterData}
                onHandleFilter={(resetFilter) => handleFilter(resetFilter)}
                listNameSpace={'economic-indexers-filter'}
                optionsEconomicIndexers={defaultIndexersList}
                listUrl='/management/58/economic-indexers'
                isAdmin
                idApplication={675}
                idDomainList={1124}
                filterNamePrefix={getDescription("DOMAINS", 1074, { textFormat: 'uppercase' })}
                defaultFilterName={getDescription("DOMAINS", 1075, { textFormat: 'uppercase' })}
              // exportList={() => exportList(location.search || undefined)}
              />

              <>
                <ResponsiveTable
                  onSortTable={handleSortList}
                  formikRef={formik?.current}
                  columns={columns}
                  data={listData}
                />

                {!listData.length && (
                  <S.ButtonContainer style={{ minHeight: '450px' }}>
                    <Text style={{
                      fontWeight: 'bold',
                      color: Colors.GRAY[1],
                      margin: 'auto',
                      cursor: 'pointer',
                      marginTop: '10px'
                    }}
                      onClick={() => { }}
                    >
                      {getDescription("DOMAINS", 786)}
                    </Text>
                  </S.ButtonContainer>
                )}
              </>
            </Form>
          )}
        </Formik>

        <S.LoadContainer>
          {!listData?.length || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              {getDescription("DOMAINS", 784, 'uppercase')}
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage(paginationControls)}>
              + {getDescription("DOMAINS", 785, 'uppercase')}
            </Button>
          )}
        </S.LoadContainer>
      </S.Content>
    </>
  );
}
